<style scoped>
.email-alert {
  margin-left: 15px;
  margin-right: 15px;
}
</style>
<template>
  <b-modal
    :id="id"
    ref="modal"
    title="Password Recovery"
    header-class="anchor-modal"
    hide-footer
    no-close-on-backdrop
    static
  >
    <form-wizard
      color="#3e88c9"
      subtitle=""
      error-color="#a94442"
      ref="wizard"
      stepSize="xs"
      @on-complete="submit"
      @keyup.enter.native="$refs.wizard.nextTab()"
    >
      <h4 slot="title" class="sr-only"> Password Recovery </h4>
      <tab-content title="Email Confirmation" :before-change="validateEmail" v-if="showFirstStep">
        <b-alert show variant="primary" class="email-alert">
          Please enter the email address you use to log in, and we’ll send you
          a 6-digit verification code to reset your password.
        </b-alert>
        <vue-form-generator
          :schema="schemas.email.schema"
          :model="model"
          :options="schemas.email.options"
          ref="emailForm"
        ></vue-form-generator>
      </tab-content>
      <tab-content title="6-Digit Code" :before-change="validateCode">
        <vue-form-generator
          :schema="schemas.code.schema"
          :model="model"
          :options="schemas.code.options"
          ref="codeForm"
        ></vue-form-generator>
        <div class="col-md-12 text-small">
          <div v-if="showResend">
            If you did not receive an email,
            <a href="#" @click.prevent="resendCode">click here</a>
            to re-send the link.
          </div>
          <div v-else>
            {{ resendFeedback }}
          </div>
        </div>
      </tab-content>
      <tab-content title="New Password" :before-change="validatePass">
        <vue-form-generator
          :schema="schemas.pass.schema"
          :model="model"
          :options="schemas.pass.options"
          ref="passForm"
        ></vue-form-generator>
      </tab-content>

      <template slot="footer" slot-scope="props">
        <b-col class="text-center mt-3">
          <wizard-button
            v-if="props.activeTabIndex > 0"
            class="d-inline mr-3 previous-button"
            @click.native="props.prevTab()">
            <font-awesome-icon icon="chevron-left" />
              Previous
          </wizard-button>
          <wizard-button
            v-if="shouldDisplayNext"
            class="d-inline mr-3 next-button"
            @click.native="props.nextTab()"
            :style="props.fillButtonStyle">
              Next
              <font-awesome-icon icon="chevron-right" />
          </wizard-button>
          <wizard-button
            v-if="props.isLastStep"
            class="d-inline mr-3 finish-button"
            @click.native="props.nextTab()"
            :style="props.fillButtonStyle"
          >
              <font-awesome-icon icon="check" />
              Done
          </wizard-button>
        </b-col>
      </template>
    </form-wizard>
  </b-modal>
</template>

<script>
import { FormWizard, TabContent, WizardButton } from 'vue-form-wizard';
import VueFormGenerator from 'vue-form-generator';

import helper from '@/libs/helper';

import FP_MODEL from '@/components/auth/forgot_pass/resources/model.json';

import EMAIL_SCHEMA from '@/components/auth/forgot_pass/schemas/email';
import CODE_SCHEMA from '@/components/auth/forgot_pass/schemas/code';
import PASS_SCHEMA from '@/components/auth/forgot_pass/schemas/pass';


export default {
  components: {
    FormWizard,
    TabContent,
    WizardButton,
    'vue-form-generator': VueFormGenerator.component,
  },
  props: {
    email: String,
  },
  data() {
    return {
      id: 'password-reset-modal',
      showResend: true,
      resendFeedback: '',
      mounted: false,
      showFirstStep: true,
      schemas: {
        email: {
          schema: EMAIL_SCHEMA.schema,
          options: EMAIL_SCHEMA.options,
        },
        code: {
          schema: CODE_SCHEMA.schema,
          options: CODE_SCHEMA.options,
        },
        pass: {
          schema: PASS_SCHEMA.schema,
          options: PASS_SCHEMA.options,
        },
      },
      model: this.getModel(),
    };
  },
  mounted() {
    this.mounted = true;

    this.$root.$on('bv::modal::hide', (bvEvent) => {
      if (bvEvent.target.id === this.id) {
        this.resetModel();
      }
    });

    this.setEmail();
  },
  watch: {
    email() {
      this.setEmail();
    },
  },
  computed: {
    shouldDisplayNext() {
      if (!this.mounted) { return false; }

      const wizard = this.$refs.wizard;
      const emptyEmail = this.model.email === null || this.model.email === '';
      return wizard.activeTabIndex === 0 ? !emptyEmail : !wizard.isLastStep;
    },
  },
  methods: {
    getModel() {
      return helper.copy(FP_MODEL);
    },
    setEmail() {
      this.model.email = this.email;
    },
    resetModel() {
      const model = this.getModel();
      if (this.email) {
        model.email = this.email;
      }

      this.model = model;
      this.showFirstStep = true;
    },
    show(showFirstStep) {
      this.$refs.wizard.reset();
      if (showFirstStep === false) {
        this.$refs.wizard.changeTab(0, 1);
      }
      this.showFirstStep = showFirstStep !== false;
      const modal = this.$refs.modal;
      if (modal) {
        modal.show();
      }
    },
    hide() {
      const modal = this.$refs.modal;
      if (modal) {
        modal.hide();
      }
    },
    sendCode() {
      return new Promise((resolve, reject) => {
        this.$reqs.post('/reset_password', { email: this.model.email })
          .then(() => resolve(true))
          .catch(() => reject());
      });
    },
    resendCode() {
      const displayFeedback = (message) => {
        this.resendFeedback = message;
        this.showResend = false;
        setTimeout(() => {
          this.resendFeedback = '';
          this.showResend = true;
        }, 3 * 1000);
      };

      this.sendCode()
        .then(() => {
          displayFeedback('A new 6-digit code was sent to your email.');
        })
        .catch(() => {
          displayFeedback('We could not send the 6-digit code to your email.');
        });
    },
    validateEmail() {
      this.model.email = this.model.email.trim();
      if (this.$refs.emailForm.validate()) {
        return this.sendCode();
      }

      return false;
    },
    validateCode() {
      if (this.model.code) {
        this.model.code = this.model.code.trim();
        if (this.$refs.codeForm.validate()) {
          return new Promise((resolve, reject) => {
            const data = {
              email: this.model.email,
              code: this.model.code,
            };
            this.$reqs.post('/verify_reset_code', data)
              .then((result) => {
                this.model.token = result.data.data.resetToken;
                resolve(true);
              })
              .catch(() => reject());
          });
        }
      }

      return false;
    },
    validatePass() {
      return this.$refs.passForm.validate();
    },
    submit() {
      const data = {
        email: this.model.email,
        password: this.model.password,
        resetToken: this.model.token,
      };
      this.$reqs.post('/new_password', data)
        .then((result) => {
          this.$emit('on-success', result);
        })
        .catch(() => {});
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/auth/forgot_pass/ForgotPassModal.vue